import type {
  AdminUserMetabaseCollection,
  AdminUserMetabaseCollectionDashboard,
  PortalOption
} from '@branditdev/mf-types'
import { queryOptions } from '@tanstack/react-query'
import branditRails from '../../api/brandit_rails'

export type MetabaseResourceId = string | number

export type AdminUserMetabaseDashboardGuestEmbed = {
  token: string
  expires_at: string | null
  metabase_site_url: string
}

export const metabaseResourceIdPresent = (
  resourceId: MetabaseResourceId | null | undefined
): resourceId is MetabaseResourceId => resourceId !== null && resourceId !== undefined && resourceId !== ''

const adminUserDashboardKeys = {
  all: ['adminUserDashboard'] as const,
  portals: () => [...adminUserDashboardKeys.all, 'portals'] as const,
  metabaseCollections: () => [...adminUserDashboardKeys.all, 'metabaseCollections'] as const,
  metabaseCollectionDashboards: (collectionId: MetabaseResourceId | null | undefined) =>
    [...adminUserDashboardKeys.metabaseCollections(), collectionId, 'dashboards'] as const,
  metabaseDashboardGuestEmbed: (
    collectionId: MetabaseResourceId | null | undefined,
    dashboardId: MetabaseResourceId | null | undefined
  ) => [...adminUserDashboardKeys.metabaseCollectionDashboards(collectionId), dashboardId, 'guestEmbed'] as const
}

const fetchAdminUserPortals = async (): Promise<PortalOption[]> => {
  const { data } = await branditRails.get<PortalOption[]>('admin_user/portals')
  return Array.isArray(data) ? data : []
}

const fetchAdminUserMetabaseCollections = async (): Promise<AdminUserMetabaseCollection[]> => {
  const { data } = await branditRails.get<AdminUserMetabaseCollection[]>('admin_user/metabase_collections')
  return Array.isArray(data) ? data : []
}

const fetchAdminUserMetabaseCollectionDashboards = async (
  collectionId: MetabaseResourceId | null | undefined
): Promise<AdminUserMetabaseCollectionDashboard[]> => {
  if (!metabaseResourceIdPresent(collectionId)) {
    throw new Error('Collection id is required')
  }

  const { data } = await branditRails.get<{ dashboards?: AdminUserMetabaseCollectionDashboard[] }>(
    `admin_user/metabase_collections/${collectionId}/dashboards`
  )
  return Array.isArray(data?.dashboards) ? data.dashboards : []
}

const fetchAdminUserMetabaseDashboardGuestEmbed = async ({
  collectionId,
  dashboardId
}: {
  collectionId: MetabaseResourceId | null | undefined
  dashboardId: MetabaseResourceId | null | undefined
}): Promise<AdminUserMetabaseDashboardGuestEmbed> => {
  if (!metabaseResourceIdPresent(collectionId)) {
    throw new Error('Collection id is required')
  }
  if (!metabaseResourceIdPresent(dashboardId)) {
    throw new Error('Dashboard id is required')
  }

  const { data } = await branditRails.get<AdminUserMetabaseDashboardGuestEmbed>(
    `admin_user/metabase_collections/${collectionId}/dashboards/${encodeURIComponent(String(dashboardId))}/guest_embed`
  )

  if (!data?.token || !data?.metabase_site_url) {
    throw new Error('Missing embed response')
  }

  return data
}

export const adminUserPortalsQueryOptions = () =>
  queryOptions({
    queryKey: adminUserDashboardKeys.portals(),
    queryFn: fetchAdminUserPortals
  })

export const adminUserMetabaseCollectionsQueryOptions = () =>
  queryOptions({
    queryKey: adminUserDashboardKeys.metabaseCollections(),
    queryFn: fetchAdminUserMetabaseCollections
  })

export const adminUserMetabaseCollectionDashboardsQueryOptions = (
  collectionId: MetabaseResourceId | null | undefined
) =>
  queryOptions({
    queryKey: adminUserDashboardKeys.metabaseCollectionDashboards(collectionId),
    queryFn: () => fetchAdminUserMetabaseCollectionDashboards(collectionId)
  })

export const adminUserMetabaseDashboardGuestEmbedQueryOptions = ({
  collectionId,
  dashboardId
}: {
  collectionId: MetabaseResourceId | null | undefined
  dashboardId: MetabaseResourceId | null | undefined
}) =>
  queryOptions({
    queryKey: adminUserDashboardKeys.metabaseDashboardGuestEmbed(collectionId, dashboardId),
    queryFn: () => fetchAdminUserMetabaseDashboardGuestEmbed({ collectionId, dashboardId })
  })
